/**
 * Prospects Store (Pinia)
 *
 * @version 1.0.0
 * @description Manages prospect/lead CRUD, highlighted count, ownership check, and export
 * @migrated-from store/prospects/prospects.ts (Vuex 4)
 * @note WebSocket target — websocket store dispatches updates to this store
 */

import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage } from '@/utils/utils'
import { useNavigationStore } from '@/stores/useNavigationStore'
import type { Pagination, ApiRequestOptions } from '@/stores/types'

// =============================================================================
// Types (re-exported from @/models for backward compatibility)
// =============================================================================

import type { Prospect, FormState } from '@/models'
export type { Prospect }

export type ProspectPayload = Omit<FormState<Prospect>, 'created_at' | 'updated_at'> & Record<string, unknown>

// =============================================================================
// Store
// =============================================================================

export const useProspectsStore = defineStore('prospects', () => {
  const { companyApiUrl } = storeToRefs(useNavigationStore())
  const baseUrl = import.meta.env.VITE_API_URL as string

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const loading = ref(false)
  const prospects = ref<Prospect[]>([])
  const prospectsHighlighted = ref(0)
  const prospectsPagination = ref<Pagination | null>(null)
  const currentOptions = ref<ApiRequestOptions>({})

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Fetch a single prospect by ID.
   */
  async function getProspect(prospectId: number | string): Promise<Prospect> {
    loading.value = true
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/prospect/${prospectId}`)
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch prospects list with pagination/filter options.
   */
  async function fetchProspects(options?: ApiRequestOptions): Promise<any> {
    loading.value = true
    if (options != null) currentOptions.value = options

    try {
      const { data } = await axios.get(`${companyApiUrl.value}/prospect`, {
        params: options ?? currentOptions.value,
      })
      prospects.value = data.data ?? data
      prospectsPagination.value = data.pagination ?? null
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch count of highlighted/flagged prospects.
   */
  async function fetchHighlightedCount(): Promise<number> {
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/prospect/highlighted`)
      prospectsHighlighted.value = data
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /**
   * Check if a prospect belongs to the current user.
   */
  async function isProspectMine(prospectId: number | string): Promise<boolean> {
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/prospect/${prospectId}/is-mine`)
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /**
   * Create a new prospect, then refresh the list.
   */
  async function storeProspect(prospect: ProspectPayload): Promise<any> {
    if (prospect == null) throw { message: 'Prospect not applicable' }

    loading.value = true
    try {
      const { data } = await axios.post(`${companyApiUrl.value}/prospect`, prospect)
      await fetchProspects()
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Update an existing prospect.
   */
  async function patchProspect(prospect: ProspectPayload & { id: number }): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.put(`${companyApiUrl.value}/prospect/${prospect.id}`, prospect)
      prospects.value = prospects.value.map((item) => (item.id === data.id ? data : item))
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Export prospects to Excel (triggers browser download).
   */
  async function exportProspects(options?: ApiRequestOptions): Promise<void> {
    try {
      const { data } = await axios.get(`${baseUrl}/prospect/export-all`, {
        params: options ?? currentOptions.value,
        responseType: 'blob',
      })
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `${Date.now()}_prospects.xlsx`
      link.click()
      window.URL.revokeObjectURL(link.href)
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /**
   * Update a prospect in the local list (used by WebSocket handler).
   */
  function updateProspectLocally(prospect: Prospect): void {
    prospects.value = prospects.value.map((item) => (item.id === prospect.id ? prospect : item))
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    loading,
    prospects,
    prospectsHighlighted,
    prospectsPagination,
    currentOptions,

    // Actions
    getProspect,
    fetchProspects,
    fetchHighlightedCount,
    isProspectMine,
    storeProspect,
    patchProspect,
    exportProspects,

    // WebSocket helpers
    updateProspectLocally,
  }
})
